import React, { useState, useMemo } from 'react';
import {
  Button,
  Card,
  Select,
  Stack,
  Flex,
  useTheme,
  spacing,
} from '@fti/design-system';

export type LayoutEngine =
  | 'd3-force-worker'
  | 'fcose'
  | 'd3-force'
  | 'cose-bilkent'
  | 'cose'
  | 'circle'
  | 'concentric'
  | 'breadthfirst'
  | 'grid';

export interface FcoseParams {
  nodeRepulsion: number;
  idealEdgeLength: number;
  edgeElasticity: number;
  gravity: number;
  gravityRange: number;
  numIter: number;
  nestingFactor: number;
  nodeSeparation: number;
  tile: boolean;
  randomize: boolean;
}

export interface D3ForceParams {
  linkDistance: number;
  linkStrength: number;
  manyBodyStrength: number;
  centerStrength: number;
  collideRadius: number;
  collideStrength: number;
  alpha: number;
  alphaDecay: number;
  velocityDecay: number;
  infinite: boolean;
}

export interface CoseBilkentParams {
  nodeRepulsion: number;
  idealEdgeLength: number;
  gravity: number;
  gravityRange: number;
  numIter: number;
  tilingPadding: number;
  randomize: boolean;
}

export interface SimpleParams {
  spacingFactor: number;
}

export type LayoutParams = FcoseParams | D3ForceParams | CoseBilkentParams | SimpleParams;

export const DEFAULT_FCOSE: FcoseParams = {
  // Default tuned for large investigation graphs (2k+ nodes):
  // max node repulsion + max ideal edge length for generous spacing.
  nodeRepulsion: 50000,
  idealEdgeLength: 400,
  edgeElasticity: 0.45,
  gravity: 0.25,
  gravityRange: 3.8,
  numIter: 2500,
  nestingFactor: 0.1,
  nodeSeparation: 75,
  tile: true,
  randomize: true,
};

export const DEFAULT_D3_FORCE: D3ForceParams = {
  // Tuned for large investigation graphs with the web-worker engine:
  // max link distance + softer link strength spreads hub clusters out;
  // stronger center pull keeps disconnected components on-screen;
  // tighter collision reduces node overlap. Converge-and-stop by default
  // (infinite:false) so CPU drops once the simulation settles — the user can
  // still drag nodes individually, they just won't re-heat the whole sim.
  linkDistance: 400,
  linkStrength: 0.5,
  manyBodyStrength: -750,
  centerStrength: 0.1,
  collideRadius: 30,
  collideStrength: 0.9,
  alpha: 1,
  alphaDecay: 0.0228,
  velocityDecay: 0.4,
  infinite: false,
};

export const DEFAULT_COSE_BILKENT: CoseBilkentParams = {
  nodeRepulsion: 4500,
  idealEdgeLength: 200,
  gravity: 0.08,
  gravityRange: 3.5,
  numIter: 1800,
  tilingPadding: 10,
  randomize: true,
};

export const DEFAULT_SIMPLE: SimpleParams = { spacingFactor: 1.2 };

const ENGINE_OPTIONS = [
  { value: 'd3-force-worker', label: 'D3 force (Web Worker — non-blocking)' },
  { value: 'fcose', label: 'ForceAtlas2 (fCoSE) — synchronous' },
  { value: 'd3-force', label: 'D3 forceSimulation — synchronous' },
  { value: 'cose-bilkent', label: 'CoSE-Bilkent' },
  { value: 'cose', label: 'CoSE (legacy)' },
  { value: 'circle', label: 'Circle' },
  { value: 'concentric', label: 'Concentric' },
  { value: 'breadthfirst', label: 'Hierarchical (BFS)' },
  { value: 'grid', label: 'Grid' },
];

interface LayoutPanelProps {
  engine: LayoutEngine;
  onEngineChange: (e: LayoutEngine) => void;
  fcose: FcoseParams;
  onFcoseChange: (p: FcoseParams) => void;
  d3: D3ForceParams;
  onD3Change: (p: D3ForceParams) => void;
  coseBilkent: CoseBilkentParams;
  onCoseBilkentChange: (p: CoseBilkentParams) => void;
  simple: SimpleParams;
  onSimpleChange: (p: SimpleParams) => void;
  onApply: () => void;
  onReset: () => void;
  running?: boolean;
  onStop?: () => void;
}

function NumberField({
  label,
  value,
  min,
  max,
  step,
  onChange,
  hint,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  hint?: string;
}) {
  const { theme } = useTheme();
  return (
    <div>
      <Flex justify="between" align="center" style={{ marginBottom: '2px' }}>
        <span style={{ fontSize: '0.75rem', fontWeight: 500, color: theme.colors.text.primary }}>
          {label}
        </span>
        <span style={{ fontSize: '0.7rem', color: theme.colors.text.secondary, fontFamily: 'monospace' }}>
          {value}
        </span>
      </Flex>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{
          width: '100%',
          accentColor: theme.colors.semantic.info.default,
        }}
      />
      {hint && (
        <div style={{ fontSize: '0.65rem', color: theme.colors.text.secondary, marginTop: '1px' }}>
          {hint}
        </div>
      )}
    </div>
  );
}

function BooleanField({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  hint?: string;
}) {
  const { theme } = useTheme();
  return (
    <div>
      <Flex align="center" gap="xs">
        <input
          type="checkbox"
          checked={value}
          onChange={(e) => onChange(e.target.checked)}
          style={{ accentColor: theme.colors.semantic.info.default, margin: 0 }}
        />
        <span style={{ fontSize: '0.75rem', fontWeight: 500, color: theme.colors.text.primary }}>
          {label}
        </span>
      </Flex>
      {hint && (
        <div style={{ fontSize: '0.65rem', color: theme.colors.text.secondary, marginTop: '1px', marginLeft: '22px' }}>
          {hint}
        </div>
      )}
    </div>
  );
}

export const LayoutPanel: React.FC<LayoutPanelProps> = ({
  engine,
  onEngineChange,
  fcose,
  onFcoseChange,
  d3,
  onD3Change,
  coseBilkent,
  onCoseBilkentChange,
  simple,
  onSimpleChange,
  onApply,
  onReset,
  running = false,
  onStop,
}) => {
  const { theme } = useTheme();
  const [showAdvanced, setShowAdvanced] = useState(false);

  const engineDescription = useMemo(() => {
    switch (engine) {
      case 'd3-force-worker':
        return 'D3 force simulation running in a dedicated Web Worker. The main thread stays responsive (tabs, panels, clicks all work) while physics iterate off-thread. Recommended default for large graphs.';
      case 'fcose':
        return 'Fast CoSE — a ForceAtlas2-flavor layout. Runs on the main thread; UI briefly blocks on large graphs. Use for smaller graphs where the extra polish matters.';
      case 'd3-force':
        return 'D3 forceSimulation on the main thread (via cytoscape-d3-force). Use the worker variant unless you need Cytoscape-native animation integration.';
      case 'cose-bilkent':
        return 'CoSE-Bilkent — polished quality on small-to-medium graphs (<1k nodes). Slower on larger graphs.';
      case 'cose':
        return 'Legacy CoSE — the original Cytoscape force model. Kept for compatibility.';
      case 'circle':
        return 'Nodes arranged in a circle. Good for showing cycles or small graphs at a glance.';
      case 'concentric':
        return 'Nodes arranged in concentric rings by degree. Centers the highest-degree node.';
      case 'breadthfirst':
        return 'Hierarchical top-down tree via breadth-first traversal. Good for DAG-like structures.';
      case 'grid':
        return 'Nodes on a fixed grid. Useful as a starting state before running a force layout.';
    }
  }, [engine]);

  return (
    <Card style={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Card.Body style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm, overflow: 'auto', padding: spacing.sm }}>
        <Flex justify="between" align="center">
          <h4 style={{ margin: 0, fontSize: '0.95rem', color: theme.colors.text.primary }}>Layout</h4>
          <Flex gap="xs">
            {running && onStop && (
              <Button variant="outline" size="sm" onClick={onStop}>Stop</Button>
            )}
            <Button variant="ghost" size="sm" onClick={onReset}>Reset</Button>
            <Button variant="solid" color="primary" size="sm" onClick={onApply} disabled={running}>
              {running ? 'Running...' : 'Apply'}
            </Button>
          </Flex>
        </Flex>

        <div>
          <Select
            size="sm"
            value={engine}
            onChange={(v: string) => onEngineChange(v as LayoutEngine)}
            options={ENGINE_OPTIONS}
          />
          <div style={{ fontSize: '0.7rem', color: theme.colors.text.secondary, marginTop: '6px', lineHeight: 1.35 }}>
            {engineDescription}
          </div>
        </div>

        {engine === 'fcose' && (
          <Stack gap="sm">
            <NumberField
              label="Node repulsion"
              value={fcose.nodeRepulsion}
              min={500}
              max={50000}
              step={500}
              onChange={(v) => onFcoseChange({ ...fcose, nodeRepulsion: v })}
              hint="Higher = nodes push apart harder."
            />
            <NumberField
              label="Ideal edge length"
              value={fcose.idealEdgeLength}
              min={20}
              max={400}
              step={10}
              onChange={(v) => onFcoseChange({ ...fcose, idealEdgeLength: v })}
              hint="Target distance between connected nodes."
            />
            <NumberField
              label="Gravity"
              value={fcose.gravity}
              min={0}
              max={2}
              step={0.05}
              onChange={(v) => onFcoseChange({ ...fcose, gravity: v })}
              hint="Pulls nodes toward the center."
            />
            <BooleanField
              label="Tile disconnected components"
              value={fcose.tile}
              onChange={(v) => onFcoseChange({ ...fcose, tile: v })}
              hint="Arrange separate subgraphs in a tiled layout."
            />
            <BooleanField
              label="Randomize starting positions"
              value={fcose.randomize}
              onChange={(v) => onFcoseChange({ ...fcose, randomize: v })}
              hint="Off = incremental; keeps current positions as seed."
            />
            <button
              onClick={() => setShowAdvanced((v) => !v)}
              style={{
                background: 'transparent',
                border: 'none',
                color: theme.colors.semantic.info.default,
                fontSize: '0.75rem',
                cursor: 'pointer',
                padding: 0,
                textAlign: 'left',
              }}
            >
              {showAdvanced ? '▾ Hide advanced' : '▸ Show advanced'}
            </button>
            {showAdvanced && (
              <Stack gap="sm">
                <NumberField
                  label="Edge elasticity"
                  value={fcose.edgeElasticity}
                  min={0}
                  max={2}
                  step={0.05}
                  onChange={(v) => onFcoseChange({ ...fcose, edgeElasticity: v })}
                />
                <NumberField
                  label="Gravity range"
                  value={fcose.gravityRange}
                  min={0.5}
                  max={10}
                  step={0.1}
                  onChange={(v) => onFcoseChange({ ...fcose, gravityRange: v })}
                />
                <NumberField
                  label="Iterations"
                  value={fcose.numIter}
                  min={100}
                  max={5000}
                  step={100}
                  onChange={(v) => onFcoseChange({ ...fcose, numIter: v })}
                />
                <NumberField
                  label="Nesting factor"
                  value={fcose.nestingFactor}
                  min={0}
                  max={1}
                  step={0.05}
                  onChange={(v) => onFcoseChange({ ...fcose, nestingFactor: v })}
                />
                <NumberField
                  label="Node separation"
                  value={fcose.nodeSeparation}
                  min={0}
                  max={300}
                  step={5}
                  onChange={(v) => onFcoseChange({ ...fcose, nodeSeparation: v })}
                />
              </Stack>
            )}
          </Stack>
        )}

        {(engine === 'd3-force' || engine === 'd3-force-worker') && (
          <Stack gap="sm">
            <NumberField
              label="Link distance"
              value={d3.linkDistance}
              min={20}
              max={400}
              step={10}
              onChange={(v) => onD3Change({ ...d3, linkDistance: v })}
              hint="Target distance along each edge."
            />
            <NumberField
              label="Link strength"
              value={d3.linkStrength}
              min={0}
              max={2}
              step={0.05}
              onChange={(v) => onD3Change({ ...d3, linkStrength: v })}
              hint="Stiffness of each edge."
            />
            <NumberField
              label="Charge (repulsion)"
              value={d3.manyBodyStrength}
              min={-2000}
              max={0}
              step={50}
              onChange={(v) => onD3Change({ ...d3, manyBodyStrength: v })}
              hint="More negative = stronger repulsion."
            />
            <NumberField
              label="Center strength"
              value={d3.centerStrength}
              min={0}
              max={1}
              step={0.01}
              onChange={(v) => onD3Change({ ...d3, centerStrength: v })}
              hint="Pulls the layout toward the viewport center."
            />
            <NumberField
              label="Collision radius"
              value={d3.collideRadius}
              min={0}
              max={200}
              step={5}
              onChange={(v) => onD3Change({ ...d3, collideRadius: v })}
              hint="Minimum distance between node centers."
            />
            <BooleanField
              label="Infinite simulation"
              value={d3.infinite}
              onChange={(v) => onD3Change({ ...d3, infinite: v })}
              hint="Keep running so nodes react to drags."
            />
            <button
              onClick={() => setShowAdvanced((v) => !v)}
              style={{
                background: 'transparent',
                border: 'none',
                color: theme.colors.semantic.info.default,
                fontSize: '0.75rem',
                cursor: 'pointer',
                padding: 0,
                textAlign: 'left',
              }}
            >
              {showAdvanced ? '▾ Hide advanced' : '▸ Show advanced'}
            </button>
            {showAdvanced && (
              <Stack gap="sm">
                <NumberField
                  label="Collision strength"
                  value={d3.collideStrength}
                  min={0}
                  max={1}
                  step={0.05}
                  onChange={(v) => onD3Change({ ...d3, collideStrength: v })}
                />
                <NumberField
                  label="Alpha"
                  value={d3.alpha}
                  min={0}
                  max={1}
                  step={0.01}
                  onChange={(v) => onD3Change({ ...d3, alpha: v })}
                  hint="Initial simulation energy."
                />
                <NumberField
                  label="Alpha decay"
                  value={d3.alphaDecay}
                  min={0}
                  max={0.1}
                  step={0.001}
                  onChange={(v) => onD3Change({ ...d3, alphaDecay: v })}
                  hint="How quickly the simulation cools."
                />
                <NumberField
                  label="Velocity decay"
                  value={d3.velocityDecay}
                  min={0}
                  max={1}
                  step={0.02}
                  onChange={(v) => onD3Change({ ...d3, velocityDecay: v })}
                  hint="Friction / damping per tick."
                />
              </Stack>
            )}
          </Stack>
        )}

        {engine === 'cose-bilkent' && (
          <Stack gap="sm">
            <NumberField
              label="Node repulsion"
              value={coseBilkent.nodeRepulsion}
              min={100}
              max={20000}
              step={100}
              onChange={(v) => onCoseBilkentChange({ ...coseBilkent, nodeRepulsion: v })}
            />
            <NumberField
              label="Ideal edge length"
              value={coseBilkent.idealEdgeLength}
              min={20}
              max={500}
              step={10}
              onChange={(v) => onCoseBilkentChange({ ...coseBilkent, idealEdgeLength: v })}
            />
            <NumberField
              label="Gravity"
              value={coseBilkent.gravity}
              min={0}
              max={1}
              step={0.01}
              onChange={(v) => onCoseBilkentChange({ ...coseBilkent, gravity: v })}
            />
            <NumberField
              label="Gravity range"
              value={coseBilkent.gravityRange}
              min={0.5}
              max={10}
              step={0.1}
              onChange={(v) => onCoseBilkentChange({ ...coseBilkent, gravityRange: v })}
            />
            <NumberField
              label="Iterations"
              value={coseBilkent.numIter}
              min={100}
              max={5000}
              step={100}
              onChange={(v) => onCoseBilkentChange({ ...coseBilkent, numIter: v })}
            />
            <NumberField
              label="Tiling padding"
              value={coseBilkent.tilingPadding}
              min={0}
              max={100}
              step={2}
              onChange={(v) => onCoseBilkentChange({ ...coseBilkent, tilingPadding: v })}
            />
            <BooleanField
              label="Randomize"
              value={coseBilkent.randomize}
              onChange={(v) => onCoseBilkentChange({ ...coseBilkent, randomize: v })}
            />
          </Stack>
        )}

        {(engine === 'cose' || engine === 'circle' || engine === 'concentric' || engine === 'breadthfirst' || engine === 'grid') && (
          <Stack gap="sm">
            <NumberField
              label="Spacing factor"
              value={simple.spacingFactor}
              min={0.5}
              max={5}
              step={0.1}
              onChange={(v) => onSimpleChange({ ...simple, spacingFactor: v })}
              hint="Multiplier applied to default node spacing."
            />
          </Stack>
        )}
      </Card.Body>
    </Card>
  );
};
